import { useEffect, useState, useCallback, useRef } from 'react'
import { ExternalLink, Gift, Ruler, Tag, Link2, Heart, ChevronLeft, ChevronRight, CheckCircle2, X, PartyPopper, Lock, RotateCcw, Palette } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { supabase } from '@/integrations/supabase/client'
import { fireConfetti } from '@/lib/confetti'
import { formatPrice, urlHostname, type WishlistItem } from '@/lib/wishlist'
import { useImageBackground } from '@/hooks/useImageBackground'
import { getCategoryColorClasses, getCategoryInlineStyle, translateCategory } from '@/lib/categoryColors'
import { parseItemColors } from '@/lib/itemColors'
import { useLanguage } from '@/context/LanguageContext'
import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

interface ItemDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: WishlistItem | null
  items?: WishlistItem[]
  onNavigate?: (item: WishlistItem) => void
  onItemReserved?: (updated: { id: string; is_reserved: boolean; reserved_by: string | null; reserved_session_id: string | null; purchase_count?: number }) => void
  isOwner?: boolean
  ownerDisplayName?: string
  ownerShippingAddress?: string | null
  ownerShippingRecipient?: string | null
  ownerUsername?: string | null
  isGuide?: boolean
}

function getOrCreateSessionId(): string {
  const KEY = 'mmag_session_id'
  let id = sessionStorage.getItem(KEY)
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem(KEY, id)
  }
  return id
}

export default function ItemDetailDialog({ open, onOpenChange, item, items, onNavigate, onItemReserved, isOwner = false, ownerDisplayName, ownerShippingAddress, ownerShippingRecipient, ownerUsername, isGuide = false }: ItemDetailDialogProps) {
  const { language } = useLanguage()
  const [galleryImages, setGalleryImages] = useState<string[]>([])
  const [activeImage, setActiveImage] = useState<string | null>(null)
  const [reserveStep, setReserveStep] = useState<null | 'choose' | 'named'>(null)
  const [reserverName, setReserverName] = useState('')
  const [reserving, setReserving] = useState(false)
  const [justReserved, setJustReserved] = useState(false)
  const [justUnreserved, setJustUnreserved] = useState(false)
  const [sessionId] = useState(() => getOrCreateSessionId())

  const autoBg = useImageBackground(activeImage ?? item?.image_url ?? null)
  const imageBg = item?.bg_color || autoBg
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)

  useEffect(() => {
    if (justReserved) fireConfetti()
  }, [justReserved])

  const currentIndex = items && item ? items.findIndex((i) => i.id === item.id) : -1
  const hasPrev = items && currentIndex > 0
  const hasNext = items && currentIndex >= 0 && currentIndex < (items!.length - 1)

  const goPrev = useCallback(() => {
    if (hasPrev && items && onNavigate) {
      onNavigate(items[currentIndex - 1])
    }
  }, [hasPrev, items, currentIndex, onNavigate])

  const goNext = useCallback(() => {
    if (hasNext && items && onNavigate) {
      onNavigate(items[currentIndex + 1])
    }
  }, [hasNext, items, currentIndex, onNavigate])

  useEffect(() => {
    if (!open || !item) return
    setActiveImage(item.image_url)
    setGalleryImages([])
    setReserveStep(null)
    setReserverName('')
    setJustReserved(false)
    setJustUnreserved(false)
    supabase
      .from('wishlist_item_images')
      .select('url')
      .eq('item_id', item.id)
      .order('position', { ascending: true })
      .then(({ data }) => {
        if (data) {
          const urls = (data as unknown as { url: string }[]).map((g) => g.url)
          setGalleryImages(urls)
          if (!item.image_url && urls.length > 0) setActiveImage(urls[0])
        }
      })
  }, [open, item?.id])

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, goPrev, goNext])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    const deltaY = e.changedTouches[0].clientY - touchStartY.current
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) goPrev()
      else goNext()
    }
    touchStartX.current = null
    touchStartY.current = null
  }

  const confirmRecurringPurchase = async () => {
    if (!item) return false
    const { data, error } = await supabase.rpc('increment_recurring_purchase', { _item_id: item.id })
    if (error) return false
    const count = typeof data === 'number'
      ? data
      : Number((data as { purchase_count?: number } | null)?.purchase_count ?? ((item as WishlistItem & { purchase_count?: number }).purchase_count ?? 0) + 1)
    onItemReserved?.({ id: item.id, is_reserved: false, reserved_by: null, reserved_session_id: null, purchase_count: count })
    return true
  }

  const handleReserveNamed = async () => {
    if (!item || reserving) return
    const name = reserverName.trim()
    if (!name) return
    setReserving(true)
    if ((item as WishlistItem & { is_recurring?: boolean }).is_recurring) {
      try {
        if (await confirmRecurringPurchase()) {
          setReserveStep(null)
          setJustReserved(true)
        }
      } finally {
        setReserving(false)
      }
      return
    }
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({ is_reserved: true, reserved_by: name, reserved_session_id: sessionId })
        .eq('id', item.id)
      if (!error) {
        onItemReserved?.({ id: item.id, is_reserved: true, reserved_by: name, reserved_session_id: sessionId })
        setReserveStep(null)
        setJustReserved(true)
      }
    } finally {
      setReserving(false)
    }
  }

  const handleReserveAnonymous = async () => {
    if (!item || reserving) return
    setReserving(true)
    if ((item as WishlistItem & { is_recurring?: boolean }).is_recurring) {
      try {
        if (await confirmRecurringPurchase()) {
          setReserveStep(null)
          setJustReserved(true)
        }
      } finally {
        setReserving(false)
      }
      return
    }
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({ is_reserved: true, reserved_by: 'Anonimo', reserved_session_id: sessionId })
        .eq('id', item.id)
      if (!error) {
        onItemReserved?.({ id: item.id, is_reserved: true, reserved_by: 'Anonimo', reserved_session_id: sessionId })
        setReserveStep(null)
        setJustReserved(true)
      }
    } finally {
      setReserving(false)
    }
  }

  const handleUnreserve = async () => {
    if (!item || reserving) return
    setReserving(true)
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({ is_reserved: false, reserved_by: null, reserved_session_id: null })
        .eq('id', item.id)
      if (!error) {
        onItemReserved?.({ id: item.id, is_reserved: false, reserved_by: null, reserved_session_id: null })
        setJustReserved(false)
        setJustUnreserved(true)
      }
    } finally {
      setReserving(false)
    }
  }

  const [resetting, setResetting] = useState(false)
  const [justReset, setJustReset] = useState(false)
  const handleResetRecurring = async () => {
    if (!item || resetting) return
    setResetting(true)
    try {
      const { error } = await supabase.rpc('reset_recurring_purchase', { _item_id: item.id })
      if (!error) {
        onItemReserved?.({ id: item.id, is_reserved: false, reserved_by: null, reserved_session_id: null, purchase_count: 0 })
        setJustReset(true)
        setTimeout(() => setJustReset(false), 3000)
      }
    } finally {
      setResetting(false)
    }
  }

  if (!item) return null

  const price = formatPrice(item.price)
  const originalPrice = formatPrice(item.original_price)
  const hasDiscount = originalPrice && price && (item.original_price ?? 0) > (item.price ?? 0)
  const shopHost = urlHostname(item.url)
  const isReserved = !(item as WishlistItem & { is_recurring?: boolean }).is_recurring && (item as any).is_reserved as boolean
  const reservedBy = (item as any).reserved_by as string | null
  const reservedSessionId = (item as any).reserved_session_id as string | null
  const isRecurring = Boolean((item as WishlistItem & { is_recurring?: boolean }).is_recurring)
  const purchaseCount = (item as WishlistItem & { purchase_count?: number }).purchase_count ?? 0
  const canCancelReservation = !isRecurring && isReserved && (isOwner || reservedSessionId === sessionId)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Navigation arrows - rendered in portal above overlay */}
      {open && items && items.length > 1 && (
        <DialogPortal>
          <button
            data-nav-arrow
            onClick={goPrev}
            disabled={!hasPrev}
            className="flex fixed bottom-[60px] md:bottom-auto md:top-1/2 md:-translate-y-1/2 left-[max(0.25rem,calc(50%_-_29rem))] z-[110] w-9 h-9 md:w-10 md:h-10 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-lg text-neutral-900 transition hover:bg-white hover:scale-110 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:scale-100 pointer-events-auto"
            style={{ pointerEvents: 'auto' }}
            aria-label="Precedente"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-nav-arrow
            onClick={goNext}
            disabled={!hasNext}
            className="flex fixed bottom-[60px] md:bottom-auto md:top-1/2 md:-translate-y-1/2 right-[max(0.25rem,calc(50%_-_29rem))] z-[110] w-9 h-9 md:w-10 md:h-10 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-lg text-neutral-900 transition hover:bg-white hover:scale-110 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:scale-100 pointer-events-auto"
            style={{ pointerEvents: 'auto' }}
            aria-label="Successivo"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </DialogPortal>
      )}
      <DialogContent
        className="rounded-2xl max-w-4xl max-h-[90vh] h-[100dvh] md:h-auto overflow-hidden glass-strong p-0 gap-0 [&>button]:hidden"
        onPointerDownOutside={(e) => {
          if ((e.target as HTMLElement).closest('[data-nav-arrow]')) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if ((e.target as HTMLElement).closest('[data-nav-arrow]')) e.preventDefault()
        }}
      >
        <div className="absolute top-3 right-3 z-[80]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-lg text-neutral-700 hover:bg-white hover:text-neutral-900 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          className="flex flex-col md:flex-row flex-1 min-h-0 gap-4 md:gap-6"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Image section */}
          <div className="relative md:w-1/2 flex-shrink-0" style={{ backgroundColor: imageBg }}>
            {/* Main image */}
            <AnimatePresence mode="wait">
              <motion.div
                key={item.id + (activeImage ?? '')}
                initial={{ opacity: 0.3 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0.3 }}
                transition={{ duration: 0.15 }}
                className="relative"
              >
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={item.title}
                    className="w-full h-64 sm:h-72 md:h-[68vh] object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0' }}
                  />
                ) : (
                  <div className="w-full h-64 sm:h-72 md:h-[60vh] bg-neutral-100 flex items-center justify-center">
                    <Gift className="w-24 h-24 text-neutral-300" />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Category badge on image — mobile: top-left, desktop/tablet: top-left overlaid */}
            <span className={`${getCategoryColorClasses(item.category)} absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full z-10`} style={getCategoryInlineStyle(item.category)}>
              {translateCategory(item.category, language)}
            </span>

            {/* Gallery thumbnails — mobile: below image in flow, desktop/tablet: overlaid on bottom of image */}
            {galleryImages.length > 0 && (
              <div className="flex gap-2 overflow-x-auto px-3 py-2 justify-center md:absolute md:bottom-0 md:left-0 md:right-0 md:z-10 md:bg-gradient-to-t md:from-[#F5F7F9] md:via-[#F5F7F9]/80 md:to-transparent">
                {item.image_url && (
                  <button
                    onClick={() => setActiveImage(item.image_url)}
                    className={`shrink-0 overflow-hidden rounded-lg border-2 transition ${activeImage === item.image_url ? 'border-neutral-900' : 'border-white/80 opacity-60 hover:opacity-100'}`}
                  >
                    <img src={item.image_url} alt="Principale" className="h-12 w-12 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0' }} />
                  </button>
                )}
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    className={`shrink-0 overflow-hidden rounded-lg border-2 transition ${activeImage === img ? 'border-neutral-900' : 'border-white/80 opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img} alt={`Foto ${idx + 1}`} className="h-12 w-12 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0' }} />
                  </button>
                ))}
              </div>
            )}

          </div>

          {/* Details section */}
          <div className="md:w-1/2 flex flex-col flex-1 min-h-0 md:max-h-[68vh] md:overflow-y-auto p-5 sm:p-6">
            {/* Scrollable content area */}
            <div className="flex flex-col md:block flex-1 md:flex-none overflow-y-auto min-h-0 pb-2">
              {/* Mobile-only title (slightly smaller than desktop) */}
              <div className="md:hidden text-center">
                <h2 className="text-xl font-extrabold tracking-tight">{item.title}</h2>
                {isRecurring && (
                  <p className="mt-0.5 text-[12px] font-bold text-green-600">{language === 'en' ? 'Recurring purchases allowed' : 'Consentito acquisto ricorrente'}</p>
                )}
              </div>

              <div className="hidden md:block mt-3">
                <h2 className="text-2xl font-extrabold tracking-tight">{item.title}</h2>
                {isRecurring && (
                  <p className="mt-0.5 text-[12px] font-bold text-green-600">{language === 'en' ? 'Recurring purchases allowed' : 'Consentito acquisto ricorrente'}</p>
                )}
              </div>

              {item.description && (
                <p className="mt-3 md:mt-3 order-2 md:order-none whitespace-pre-line text-muted-foreground leading-relaxed text-sm">
                  {item.description}
                </p>
              )}

              <div className="mt-3 md:mt-5 order-1 md:order-none grid grid-cols-3 gap-3">
                <div className="glass rounded-xl p-3 text-center">
                  <div className="w-8 h-8 mx-auto rounded-xl bg-neutral-100 text-neutral-900 flex items-center justify-center">
                    <Ruler className="w-4 h-4" />
                  </div>
                  <p className="mt-2 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">{language === 'en' ? 'Size' : 'Misura'}</p>
                  <p className="mt-0.5 text-sm font-bold">{item.size ?? '\u2014'}</p>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <div className="w-8 h-8 mx-auto rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                    <Tag className="w-4 h-4" />
                  </div>
                  <p className="mt-2 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">{language === 'en' ? 'Price' : 'Prezzo'}</p>
                  <div className="mt-0.5 flex items-baseline justify-center gap-1.5">
                    {hasDiscount && (
                      <span className="text-xs text-neutral-400 line-through">{originalPrice}</span>
                    )}
                    <span className="text-sm font-bold gradient-text-price">{price ?? '\u2014'}</span>
                  </div>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <div className="w-8 h-8 mx-auto rounded-xl bg-neutral-100 text-neutral-900 flex items-center justify-center">
                    {isGuide ? <Link2 className="w-4 h-4" /> : <Palette className="w-4 h-4" />}
                  </div>
                  <p className="mt-2 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">{isGuide ? (language === 'en' ? 'Shop' : 'Negozio') : (language === 'en' ? 'Desired color' : 'Colore desiderato')}</p>
                  {isGuide ? (
                    <p className="mt-0.5 text-sm font-bold truncate">{shopHost ?? '\u2014'}</p>
                  ) : (() => {
                    const spheres = parseItemColors(item.color, `${item.title} ${item.description ?? ''}`)
                    if (spheres.length > 0) {
                      return (
                        <div className="mt-1.5 flex items-center justify-center gap-1.5" title={item.color ?? ''}>
                          {spheres.slice(0, 3).map((hex, i) => (
                            <span key={i} className="inline-block w-4 h-4 rounded-full border border-neutral-200" style={{ backgroundColor: hex }} />
                          ))}
                        </div>
                      )
                    }
                    const colorLabel = (item.color_text ?? item.color ?? '').trim()
                    return (
                      <p className="mt-0.5 text-sm font-bold truncate">
                        {colorLabel || (shopHost ?? '\u2014')}
                      </p>
                    )
                  })()}
                </div>
              </div>

              <div className="h-4"></div>

              {/* Desktop/tablet-only reserve button (full width) */}
              {!isGuide && !isReserved && !justUnreserved && (
                <button
                  type="button"
                  onClick={() => setReserveStep('choose')}
                  className="hidden md:inline-flex w-full items-center justify-center gap-2 bg-amber-100 text-amber-700 font-bold px-6 py-3 rounded-full hover:bg-amber-200 transition-colors md:mt-6 md:mb-5 lg:mt-4 lg:mb-5"
                >
                  <Heart className="w-4 h-4" />
                  {language === 'en' ? "I'll gift this!" : 'Lo regalo io!'}
                </button>
              )}

              {/* Desktop/tablet-only shop link (full width) */}
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:inline-flex w-full items-center justify-center gap-2 bg-neutral-900 text-white font-bold px-6 py-3 rounded-full hover:bg-neutral-800 transition-colors"
                >
                  {language === 'en' ? 'Go to store' : 'Vai al negozio'}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}

              {/* Just-reserved success banner — after shop link */}
              {!isGuide && justReserved && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.3, y: -40 }}
                    animate={{
                      opacity: 1,
                      scale: [0.3, 1.15, 0.95, 1.05, 1],
                      y: [-40, 0, -8, 0],
                    }}
                    exit={{ opacity: 0, scale: 0.8, y: -10 }}
                    transition={{
                      duration: 0.7,
                      ease: 'easeOut',
                      scale: { duration: 0.7, times: [0, 0.5, 0.7, 0.85, 1] },
                      y: { duration: 0.7, times: [0, 0.5, 0.75, 1] },
                    }}
                    className="mt-4 flex items-center gap-3 rounded-xl bg-green-50 border-2 border-green-300 px-4 py-3 shadow-lg"
                  >
                    <motion.div
                      animate={{ rotate: [0, -15, 15, -10, 10, 0] }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                    >
                      <PartyPopper className="w-5 h-5 text-green-600 shrink-0" />
                    </motion.div>
                    <div className="text-sm text-green-700">
                      <p className="font-bold">{language === 'en' ? 'Reservation confirmed!' : 'Prenotazione confermata!'}</p>
                      <p className="text-green-600">
                        {(ownerShippingAddress ?? '').trim()
                          ? (language === 'en'
                            ? `Now remember to go to the store or buy it directly online and ship it to: ${[(ownerShippingRecipient ?? '').trim(), (ownerShippingAddress ?? '').trim()].filter(Boolean).join(' — ')}`
                            : `Adesso ricordati di recarti in negozio o effettuare l'acquisto direttamente online e spedirlo a: ${[(ownerShippingRecipient ?? '').trim(), (ownerShippingAddress ?? '').trim()].filter(Boolean).join(' — ')}`)
                          : (language === 'en'
                            ? `Now remember to go to the store or buy it directly online and deliver it in person to ${(ownerUsername ?? ownerDisplayName ?? 'the person who wished for it').trim()}`
                            : `Adesso ricordati di recarti in negozio o effettuare l'acquisto direttamente online e consegnarlo di persona a ${(ownerUsername ?? ownerDisplayName ?? 'chi lo ha desiderato').trim()}`)}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              {/* Existing reservation info (not just reserved) */}
              {!isGuide && isReserved && reservedBy && !justReserved && (
                <p className="mt-4 md:mt-6 md:mb-2 text-sm font-semibold text-center w-full" style={{ color: '#DE256D' }}>
                  {language === 'en' ? 'A gift from' : 'Un regalo di'} <strong>{reservedBy}</strong>
                </p>
              )}

              {/* Just-unreserved confirmation banner — after shop link */}
              {!isGuide && !isReserved && justUnreserved && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="mt-4 flex items-center gap-3 rounded-xl bg-blue-50 border border-blue-200 px-4 py-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="text-sm text-blue-700">
                      <p className="font-bold">{language === 'en' ? 'Reservation cancelled' : 'Prenotazione annullata'}</p>
                      <p className="text-blue-600">{language === 'en' ? 'The gift is available again.' : 'Il regalo è di nuovo disponibile.'}</p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              {/* Cancel reservation */}
              {!isGuide && isReserved && canCancelReservation && (
                <button
                  type="button"
                  onClick={handleUnreserve}
                  disabled={reserving}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors disabled:opacity-50"
                >
                  {reserving ? (language === 'en' ? 'Removing...' : 'Rimozione...') : (language === 'en' ? 'Cancel reservation' : 'Annulla prenotazione')}
                </button>
              )}

              {/* Locked reservation */}
              {!isGuide && isReserved && !canCancelReservation && (
                <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-neutral-400">
                  <Lock className="w-3 h-3" />
                  <span>Non puoi annullare questa prenotazione</span>
                </div>
              )}

              {/* Owner-only note when they can't cancel either */}
              {!isGuide && isReserved && isOwner && !canCancelReservation && reservedSessionId && reservedSessionId !== sessionId && (
                <p className="mt-2 text-center text-xs text-neutral-400">
                  Il proprietario puo annullare la prenotazione in qualsiasi momento.
                </p>
              )}

              {/* Owner-only reset for recurring items */}
              {isRecurring && isOwner && purchaseCount > 0 && (
                <div className="mt-4 flex flex-col items-center gap-2">
                  {justReset && (
                    <AnimatePresence>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="flex items-center gap-2 rounded-xl bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-700"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Contatore azzerato
                      </motion.div>
                    </AnimatePresence>
                  )}
                  <button
                    type="button"
                    onClick={handleResetRecurring}
                    disabled={resetting}
                    className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-neutral-400 hover:text-neutral-900 transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {resetting ? 'Azzeramento...' : 'Azzera contatore acquisti'}
                  </button>
                </div>
              )}


            </div>
            {/* End scrollable content area */}

            {/* Mobile sticky bottom action bar */}
            <div className="flex gap-2 pt-3 shrink-0 md:hidden -mx-1 px-1 pb-1">
              {!isGuide && !isReserved && !justUnreserved && (
                <button
                  type="button"
                  onClick={() => setReserveStep('choose')}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-amber-100 text-amber-700 font-bold px-3 py-2.5 rounded-full hover:bg-amber-200 transition-colors text-sm"
                >
                  <Heart className="w-4 h-4" />
                  {language === 'en' ? "I'll gift this!" : 'Lo regalo io!'}
                </button>
              )}
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-neutral-900 text-white font-bold px-3 py-2.5 rounded-full hover:bg-neutral-800 transition-colors text-sm"
                >
                  {language === 'en' ? 'Go to store' : 'Vai al negozio'}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>

          </div>
        </div>
      </DialogContent>

      {/* Reserve gift popup dialog */}
      <Dialog open={reserveStep !== null} onOpenChange={(o) => { if (!o) { setReserveStep(null); setReserverName('') } }}>
        <DialogContent className="rounded-2xl max-w-sm p-0 gap-0 overflow-hidden glass-strong [&>button]:hidden">
          <button
            type="button"
            onClick={() => { setReserveStep(null); setReserverName('') }}
            className="absolute top-3 right-3 z-[70] w-8 h-8 flex items-center justify-center rounded-full bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>

          {reserveStep === 'choose' && (
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                <Gift className="w-7 h-7 text-amber-600" />
              </div>
              <DialogHeader className="text-center mb-1">
                <DialogTitle className="text-xl font-extrabold text-center">{language === 'en' ? 'How do you want to gift?' : 'Come vuoi regalare?'}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground mb-6">
                {language === 'en' ? 'Choose whether to reveal who you are or stay anonymous.' : 'Scegli se far sapere chi sei o restare anonimo.'}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => setReserveStep('named')}
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-white font-bold px-6 py-3 rounded-full hover:bg-amber-600 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {language === 'en' ? 'Enter your name' : 'Inserisci il tuo nome'}
                </button>
                <button
                  type="button"
                  onClick={handleReserveAnonymous}
                  disabled={reserving}
                  className="w-full inline-flex items-center justify-center gap-2 bg-neutral-100 text-neutral-700 font-bold px-6 py-3 rounded-full hover:bg-neutral-200 transition-colors disabled:opacity-50"
                >
                  <Heart className="w-5 h-5" />
                  {reserving ? (language === 'en' ? 'Reserving...' : 'Prenotazione...') : (language === 'en' ? 'Anonymous gift' : 'Regalo anonimo')}
                </button>
              </div>
            </div>
          )}

          {reserveStep === 'named' && (
            <div className="p-6">
              <DialogHeader className="text-center mb-4">
                <DialogTitle className="text-xl font-extrabold text-center">{language === 'en' ? 'What is your name?' : 'Qual è il tuo nome?'}</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground text-center">
                  {language === 'en' ? 'It will be shown to the wishlist owner.' : 'Verrà mostrato al proprietario della wishlist.'}
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e) => { e.preventDefault(); handleReserveNamed() }}
                className="space-y-4"
              >
                <input
                  type="text"
                  value={reserverName}
                  onChange={(e) => setReserverName(e.target.value)}
                  placeholder={language === 'en' ? 'Your name' : 'Il tuo nome'}
                  maxLength={50}
                  autoFocus
                  className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setReserveStep('choose')}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-neutral-100 text-neutral-700 font-bold px-4 py-3 rounded-full hover:bg-neutral-200 transition-colors"
                  >
                    {language === 'en' ? 'Back' : 'Indietro'}
                  </button>
                  <button
                    type="submit"
                    disabled={reserving || !reserverName.trim()}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-500 text-white font-bold px-4 py-3 rounded-full hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {reserving ? (language === 'en' ? 'Reserving...' : 'Prenotazione...') : (language === 'en' ? 'Confirm' : 'Conferma')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
